import { QueryKey, useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { QUERY_KEYS } from '../constants';
import { useInvalidateQuery } from '../hooks/useInvalidateQuery';
import { deleteData, postData } from '../utils/api';
import { EntityType } from '../@types';
import useLocalization from '../hooks/useLocalization';

type MutationArgs = {
  id: string;
  entityType: EntityType;
  isAdd: boolean;
  mutationAction: (url: string, body: unknown) => Promise<unknown>;
};

const invalidateQueriesMap: Record<EntityType, QueryKey> = {
  'market-types': [QUERY_KEYS.markets],
  events: [QUERY_KEYS.favoriteEvents, QUERY_KEYS.publishedEvents, QUERY_KEYS.tournaments],
  tournaments: [QUERY_KEYS.tournaments, QUERY_KEYS.favoriteEvents, QUERY_KEYS.publishedEvents],
};

export const useToggleFavorite = (entityType: EntityType, entityId?: string) => {
  const invalidateQuery = useInvalidateQuery();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLocalization();
  const mutationKey = ['toggle-favorite', entityType, entityId];
  const isPending = useIsMutating({ mutationKey, exact: true }) > 0;

  const mutationFn = ({ id, entityType, mutationAction }: MutationArgs) =>
    mutationAction(`user-settings/${entityType}/favourites`, { ids: [id] });

  const { mutate } = useMutation({
    mutationKey,
    mutationFn: mutationFn,
    onError: () => {
      enqueueSnackbar(t('responseMessages.unexpectedErrorOccurred'), { variant: 'error' });
    },
    onSettled: (_data, _error, { entityType }) => {
      return invalidateQuery(invalidateQueriesMap[entityType]);
    },
  });

  const toggleFavorite = (id: string, entityType: EntityType, isAdd: boolean) => {
    // Check the cache synchronously, including actions before React rerenders.
    if (queryClient.isMutating({ mutationKey, exact: true })) return;
    const mutationAction = isAdd ? postData : deleteData;
    mutate({ id, entityType, isAdd, mutationAction });
  };

  return { toggleFavorite, isPending };
};
